import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import LOOutcomeModalCst from "@components/LOOutcomeModalCst";
import CourseWizard from "../Course/CourseWizard";
import { useCourseDetailV2 } from "../Course/hooks/useCourseDetailV2";
import { useEditCourseV2 } from "../Course/hooks/useEditCourseV2";
import { useSubmitCourseV2 } from "../Course/hooks/useSubmitCourseV2";
import { CourseWizardData } from "../Course/types";

interface EditCourseProps {
  duplication?: boolean;
}

const EditCourse: React.FC<EditCourseProps> = ({ duplication = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation("wizard-course");
  const { id: corporateId = "", idCourse = "" } = useParams();
  const catalogUrl = `/v2/corporate/${corporateId}/detail/catalog`;

  const [successOpen, setSuccessOpen] = useState(false);

  const {
    wizardData,
    initialInitiativeIds,
    initialEditorial,
    isLoading,
    error,
    mutate,
  } = useCourseDetailV2({ corporateId, idCourse });

  const { submit: submitEdit, isSubmitting: isSubmittingEdit } =
    useEditCourseV2({
      corporateId,
      loId: Number(idCourse),
      initialInitiativeIds,
      initialEditorial,
    });

  const { submit: submitAdd, isSubmitting: isSubmittingAdd } =
    useSubmitCourseV2({ corporateId });

  const isSubmitting = duplication ? isSubmittingAdd : isSubmittingEdit;

  const initialData = useMemo<CourseWizardData | null>(() => {
    if (!wizardData) return null;
    if (!duplication) return wizardData;
    return {
      ...wizardData,
      title: `${wizardData.title || ""} - Copied`,
      publish_date: null,
    };
  }, [wizardData, duplication]);

  const handleComplete = async (data: CourseWizardData) => {
    try {
      if (duplication) {
        await submitAdd(data);
      } else {
        await submitEdit(data);
      }
      setSuccessOpen(true);
    } catch (err) {
      console.error(
        duplication
          ? "[wizard-course] duplicate failed"
          : "[wizard-course] edit failed",
        err,
      );
      throw err;
    }
  };

  const handleDismissSuccess = () => {
    if (!duplication) void mutate();
    navigate(catalogUrl);
  };

  if (isLoading || (!initialData && !error)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !initialData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          color: theme.palette.text.tertiary,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          {t("edit-load-error.title") || "Impossibile caricare il corso"}
        </Typography>
        <Typography sx={{ fontSize: "13px" }}>
          {error || t("edit-load-error.description") || ""}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <CourseWizard
        initialData={initialData}
        onComplete={handleComplete}
        isSubmitting={isSubmitting}
        mode={duplication ? "add" : "edit"}
      />
      <LOOutcomeModalCst
        open={successOpen}
        entityType="course"
        mode={duplication ? "add" : "edit"}
        onDismiss={handleDismissSuccess}
      />
    </>
  );
};

export default EditCourse;
